import { Button } from "@mui/base";
import React, { useState, useEffect } from "react";
import ColorPicker from "react-pick-color";
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    Grid,
    TextField,
    FormControlLabel,
    Checkbox,
    Typography
} from "@mui/material";

function AddNewRating({
    open,
    handleClose,
    addNewRatingToList,
    editData = null,
    onSaveEdit = null,
    existingRatings = [] // Default to an empty array
}) {
    const [color, setColor] = useState("#FF9900");
    const [tagName, setTagName] = useState("");
    const [definition, setDefinition] = useState("");
    const [includeInSummary, setIncludeInSummary] = useState(false);
    const [showError, setShowError] = useState(false);
    const [nameError, setNameError] = useState(""); // To hold the inline error message

    const handleDialogClose = () => {
        handleClose();
        setTagName("");
        setDefinition("");
        setColor("#FF9900");
        setIncludeInSummary(false);
        setShowError(false);
        setNameError(""); // Reset name error
    };

    useEffect(() => {
        if (editData) {
            setTagName(editData.name || "");
            setDefinition(editData.definition || "");
            setColor(editData.color || "#FF9900");
            setIncludeInSummary(editData.include_in_summary || false);
        } else {
            setTagName("");
            setDefinition("");
            setColor("#FF9900");
            setIncludeInSummary(false);
        }
        setShowError(false);
        setNameError(""); // Reset name error when dialog opens
    }, [editData]);

    const trimmedName = (tagName || "").trim();
    const trimmedDefinition = (definition || "").trim();
    const isEmpty = trimmedName.length === 0;
    const isValid = !isEmpty;

    // Check if a rating with the same name already exists
    const doesNameExist = (name) => {
        // Use optional chaining to safely access the 'existingRatings' array
        return existingRatings?.some((rating) => rating?.name?.trim()?.toLowerCase() === name?.toLowerCase());
    };

    const handleAddTag = () => {
        setShowError(true);
        if (!isValid) return;

        if (doesNameExist(trimmedName)) {
            setNameError("A rating with this name already exists."); // Set error message
            return;
        }

        addNewRatingToList({
            name: trimmedName,
            color: color,
            definition: trimmedDefinition,
            include_in_summary: includeInSummary
        });

        handleDialogClose();
    };

    const handleAddOrEditTag = () => {
        setShowError(true);
        if (!isValid) return;

        if (doesNameExist(trimmedName) && (!editData || trimmedName !== editData.name)) {
            setNameError("A rating with this name already exists.");
            return;
        }

        const payload = {
            name: trimmedName,
            color: color,
            definition: trimmedDefinition,
            include_in_summary: includeInSummary,
        };

        if (editData && onSaveEdit) {
            onSaveEdit(editData.id, payload); // Edit existing tag
        } else {
            addNewRatingToList(payload); // Add new tag
        }
        handleDialogClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleDialogClose}
            aria-labelledby="category-modal"
            aria-describedby="category-modal-description"
            disableScrollLock
            keepMounted
            className="dialog-container-rating-library"
        >
            <DialogTitle
                className="comment-library-dialog-title"
                sx={{
                    p: "16px !important",
                    mb: 2,
                    fontSize: "24px",
                    fontWeight: 600,
                    color: "#252525",
                    fontFamily: "Open Sans"
                }}
            >
                {editData ? "Edit Rating" : "New Rating"}
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ padding: "24px 32px 24px 32px !important" }}>
                <Grid container spacing={2} direction={{ xs: "column", md: "row", lg: "row" }}>
                    <Grid item xs={12} md={6} lg={7}>
                        <FormControl variant="standard" fullWidth>
                            {/* Name Field */}
                            <label className="input-form-label" style={{ fontSize: "15px", fontWeight: 600 }}>
                                Name
                            </label>
                            <TextField
                                size="small"
                                variant="outlined"
                                type="text"
                                onChange={(e) => setTagName(e.target.value)}
                                value={tagName}
                                error={showError && (isEmpty || nameError)}
                                helperText={showError && (isEmpty ? "* Name is required" : nameError)}
                                FormHelperTextProps={{
                                    sx: { margin: 0, padding: "4px 0px", lineHeight: "16px" }
                                }}
                                sx={{ width: "100%", marginBottom: "16px" }}
                            />

                            {/* Definition Field */}
                            <label className="input-form-label" style={{ fontSize: "15px", fontWeight: 600 }}>
                                Definition
                            </label>
                            <TextField
                                size="small"
                                variant="outlined"
                                multiline
                                rows={3}
                                value={definition}
                                onChange={(e) => setDefinition(e.target.value)}
                                sx={{ width: "100%", marginBottom: "16px" }}
                            />

                            {/* Include in Summary */}
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={includeInSummary}
                                        onChange={(e) => setIncludeInSummary(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label={
                                    <Box>
                                        <Typography fontWeight={600}>Include in summary</Typography>
                                        <Typography fontSize={"13px"} color="text.secondary">
                                            Any comment or line with this rating assigned will be included in the report summary.
                                        </Typography>
                                    </Box>
                                }
                                sx={{ alignItems: "flex-start", mt: 1 }}
                            />
                        </FormControl>
                    </Grid>

                    {/* Color Picker */}
                    <Grid item xs={12} md={6} lg={5}>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "end",
                                width: "100%",
                                height: "100%",
                            }}
                            className="custom-color-picker"
                        >
                            <ColorPicker
                                hideAlpha
                                color={color}
                                onChange={(color) => setColor(color.hex)}
                                style={{ width: "100%", height: "100%" }}
                                theme={{
                                    width: "352px",
                                    height: "280px",
                                    borderColor: "none !important",
                                    boxShadow: "none !important"
                                }}
                            />
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>
            <Divider />
            <DialogActions className="comment-library-summary-action">
                <div className="comment-library-action-btn">
                    <Button className="cl-summary-cancel-btn" onClick={handleDialogClose}>
                        Close
                    </Button>
                    <Button
                        className={"cl-add-summary-btn"}
                        style={{ width: "130px !important" }}
                        onClick={handleAddOrEditTag}
                    >
                        {editData ? "Update" : "Save"}
                    </Button>
                </div>
            </DialogActions>
        </Dialog>
    );
}

export default AddNewRating;
